/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { useState, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useCartModification, useCart } from '@exo/frontend-features-cart-logic';
import { useWishlistModification } from '@exo/frontend-features-wishlist-logic';
import {
  renderDefaultError,
  renderDefaultLoading,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useProduct } from '../../hooks/useProduct';
import { asProductRef, ProductRef } from '../../model/product-ref';
import { useIntl } from '@exo/frontend-common-i18n';
import { useNotificationContext } from '@exo/frontend-common-notification';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { float } from '@zxing/library/esm/customTypings';

const TYPES_OK = ['sku', 'bundleofskus'];
const TYPES_OK_WHEN_AUTO = [...TYPES_OK, 'product', 'bundleofproducts'];

// TODO: We should add currency here - needed for CT at least
export const ProductAddToCartContainer = ({
  productId,
  productType,
  hasPrice,
  autoSelectSku,
  hasWishlist = true,
  onSuccess,
  currency,
  onFailure,
  render,
  renderError = renderDefaultError,
  renderLoading = renderDefaultLoading
}: Props) => {
  const refProduct = asProductRef(productId);

  const hasNeededData = !!productType && (refProduct.isId || refProduct.isPartnumber);
  const [crtqty, setCrtQty] = useState(0);
  const { createNotification: notify } = useNotificationContext();
  // TODO: This is still depending on session and config -> not really what we want
  const cart = useCartModification();
  const wishlist = useWishlistModification();
  const intl = useIntl('features.catalog.catalog-ui.pages.ProductDetailsPage');
  const { loading, data, error } = useProduct<AddToCartProduct>(
    { productId, skip: hasNeededData },
    ProductAddToCartContainer.fragment
  );
  const sessionContext = useSessionContext();
  const {
    loading: load,
    data: cartData,
    error: err
  } = useCart<CartList>({ cartId: sessionContext.cartId! }, ProductAddToCartContainer.fragment1);
  useEffect(() => {
    if (load) return;
    if (err) {
      renderError(err);
    } else if (cartData?.me?.carts?.length! > 0) {
      let prodId = data?.product?.id.split('@@')[1];
      let obj = cartData?.me.carts[0].lineItems.find(o => o.productId === prodId);
      const cartQty = obj?.quantity || 0;
      setCrtQty(cartQty);
    }
  }, [JSON.stringify(cartData), loading, error]);

  if (error) return renderError(error);
  if (!hasNeededData && (loading || !data)) return renderLoading();
  const eProductType = data?.product?.type ?? productType;
  const avalQuantity = data?.product.availability[0].availableQuantity;
  const calculateAva = () => {
    let noOfItems = [] as any;
    if (
      avalQuantity != null &&
      avalQuantity != undefined &&
      avalQuantity != 0 &&
      avalQuantity > 0
    ) {
      let conditionQualify = avalQuantity >= 10 ? 10 : avalQuantity;
      for (let i = 1; i <= conditionQualify; i++) {
        noOfItems.push(i);
      }
    } else {
      noOfItems.push('Out of Stock');
    }
    return noOfItems;
  };
  return render({
    isEnabled:
      hasPrice !== false &&
      (autoSelectSku ? TYPES_OK_WHEN_AUTO : TYPES_OK).includes(eProductType.toLowerCase()),
    noOptions: calculateAva(),
    name: data?.product?.parent?.name,
    onAddToCart: async (qty: number) => {
      const totalQty = qty + crtqty;
      if (totalQty <= data?.product.availability[0].availableQuantity) {
        cart
          .add({
            quantity: qty,
            partnumber: refProduct.isPartnumber ? refProduct.ref : data?.product?.partnumber,
            id: refProduct.isId ? refProduct.ref : data?.product?.id,
            avalQuantity: data?.product.availability[0].availableQuantity
          })
          .then(onSuccess)
          .catch(onFailure);
      } else {
        notify({
          kind: 'error',
          title: intl.msg('title.productadded', 'Oops!'),
          subtitle: 'The product quantity you have selected is more than the available quantity'
        });
      }
    },

    onAddToCartVoucher: async (
      recName: string,
      giftType: string,
      productPrice: float,
      barcode?: string
    ) => {
      cart
        .addVoucher({
          name: recName,
          giftType: giftType,
          slug: refProduct.isId ? refProduct.ref : data?.product?.id + '/' + barcode,
          taxCategory: data?.product?.parent?.taxCategory,
          productPrice: Number(productPrice).toFixed(2),
          productCurrency: currency
        })
        .then(onSuccess)
        .catch(onFailure);
    },

    onAddToFavorites: hasWishlist
      ? async (qty: number) => {
          const wishlistId = await wishlist.getOrCreateWishlist();
          wishlist
            .add(wishlistId, {
              quantity: qty,
              partnumber: refProduct.isPartnumber ? refProduct.ref : data?.product?.partnumber
            })
            // TODO: Fix message when adding to favorite instead of cart
            .then(onSuccess)
            .catch(onFailure);
        }
      : undefined
  });
};

ProductAddToCartContainer.fragment = gql`
  fragment ProductAddToCartInfo on PrdItem {
    id
    partnumber
    type
    parent {
      type
      id
      name
      taxCategory {
        typeId
        id
      }
      parentCategory {
        id
        name
      }
    }
    availability {
      status
      availableDate
      availableQuantity
    }
  }
`;

ProductAddToCartContainer.fragment1 = gql`
  fragment Cart on CrtCart {
    id
    lineItems {
      productId
      id
      quantity
    }
  }
`;

type AddToCartProduct = {
  id: string;
  partnumber: string;
  type: string;
  parent: {
    taxCategory: {
      id: string;
      typeId: string;
    };
    name: string;
  };
  availability: {
    availableQuantity: number | undefined;
  };
};

type Props = SmartComponentProps<{
  productId: ProductRef;
  productType: 'sku' | 'product' | 'bundleOfSkus' | 'bundleOfProducts';
  autoSelectSku?: boolean;
  hasWishlist?: boolean;
  hasPrice?: boolean;
  currency?: string;
  onSuccess: () => void;
  onFailure: (error: any) => void;
  render: (props: ProductAddToCartContainerRenderProps) => JSX.Element;
}>;

export type ProductAddToCartContainerRenderProps = {
  isEnabled: boolean;
  onAddToCart: (qty: number) => Promise<void>;
  onAddToFavorites?: (qty: number) => Promise<void>;
  noOptions: any[];
  isGiftCard?: boolean;
  isGiftVoucher?: boolean;
  onAddToCartForGiftCard?: () => Promise<void>;
  productPrice?: any;
  name?: string;
  onAddToCartVoucher?: (
    recName: string,
    giftType: string,
    productPrice: float,
    barcode?: string
  ) => Promise<void>;
  barcode?: string;
};

type CartList = {
  id: string;
  lineItems: {
    productId: string;
    id: string;
    quantity: number;
  }[];
};
